import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import BatchSearchDataTable from '../../components/BatchSearchDataTable';
import { clearBatchSearchData, searchForEquipmentsInBatch } from "../../redux/store/batchSearch";
import './BatchSearch.scss';
import { Spinner, useToast, Toast, CustomModal } from '@cd/sdds-common-components-react';
import _ from 'lodash';
import { LocalizationHelper } from '@cd/sdds-common-components-react';
import { InfoIconWithTooltip } from '@cd/sdds-common-components-react/lib/esm/components/assets';

type BatchSearchProps = {

}

const BatchSearch = (props: BatchSearchProps) => {
  const { toastQueue, addToast } = useToast()
  const dispatch = useAppDispatch();
  const { t } = LocalizationHelper.useLocalize();
  const { searchResults, isLoading, hasError, errorMessage, notFound, percentage } = useAppSelector<BatchSearchStateType>(state => state.batchSearchStore);
  const textAreaRef = React.createRef<HTMLTextAreaElement>();
  const [sortedSearchResults, setSortedSearchResults] = useState<Array<SimplifiedBatchSearchResultType>>([]);
  const [duplicateInfo, setDuplicateInfo] = useState<any>({});
  const [showDuplicateInfo, setShowDuplicateInfo] = useState<boolean>(false);

  useEffect(() => {
    if (searchResults && searchResults.length >= 0) {
      setSortedSearchResults(searchResults);
    }
  }, [searchResults]);

  useEffect(() => {
    if (hasError && errorMessage !== '') {
      addToast({
        title: t("TS_core:UnExpectedErrorTitle"), text: t("TS_core:UnExpectedErrorMessage"),
        type: "error"
      })
    }
  }, [hasError, errorMessage]);

const hideDuplicateInfo= ()=>{
  setShowDuplicateInfo(false)
}  

const viewDuplicateInfo= ()=>{
  setShowDuplicateInfo(true)
}

  const onSearchClick = () => {
    dispatch(clearBatchSearchData());
    let searchInput = textAreaRef.current?.value;
    if (searchInput) {
      // Clean the input
      searchInput = searchInput
        .replace(/[ ]{1,}/g, '')
        .replace(/(\r\n|\r|\n){2,}/g, '$1')
        .replace(/(\r\n|\r|\n)+$/, '');
      
      let inputs = searchInput?.split('\n');
      // Count all occurrences
      const allCounts: Record<string, number> = {};
      inputs.forEach((value: string) => {
        allCounts[value] = (allCounts[value] || 0) + 1;
      });
  
      // Filter duplicates and compute total
      const duplicateList: Record<string, number> = {};
      let totalDuplicate = 0;
      for (const key in allCounts) {
        if (allCounts[key] > 1) {
          duplicateList[key] = allCounts[key];
          totalDuplicate += allCounts[key] - 1;
        }
      }

      // Set the duplicateInfo object
      const duplicateInfo = {
        duplicateList,
        totalDuplicate
      };
      setDuplicateInfo(duplicateInfo);
      console.log("duplicates", duplicateInfo);
      
      inputs = _.uniq<string>(inputs); // dedupe before search
  
      // Limit and dispatch
      if (inputs.length <= 10000) {
        dispatch(searchForEquipmentsInBatch(inputs));
      } else {
        inputs = _.take(inputs, 10000);
        dispatch(searchForEquipmentsInBatch(inputs));
        addToast({
          title: t("TS_core:SearchInputWarning"),
          text: t("TS_core:SearchInputWarningDescription", ['10000', '10000', '10000']),
          type: "warning"
        });
      }
    }
  }
  const onClearClick = () => {
    dispatch(clearBatchSearchData());
    if (textAreaRef.current) {
      textAreaRef.current.value = "";
    }
    setDuplicateInfo({});
  }
  const onSortData = (columnId: string, sortDirection: string) => {
    if (searchResults && searchResults.length > 0) {
      let sortedResults = _.sortBy(searchResults, columnId);
      if (sortDirection === 'desc') {
        sortedResults = _.reverse(sortedResults);
      }
      setSortedSearchResults(sortedResults as Array<SimplifiedBatchSearchResultType>);
    }
  }
  const onSearch = (searchString: string) => {
    if (searchString.length > 0) {
      if (searchResults && searchResults.length > 0) {
        const splitedString = searchString.split(",");
        let filteredDataArray;
        if (splitedString && splitedString.length > 0) {
          filteredDataArray = splitedString.map((searchstr: string) => {
            searchstr = searchstr.trim();
            if (searchstr === "") return [];
            return searchResults.filter(a => _.values(a).some((b: any) => {
              if (typeof b === 'string') {
                return b?.toLocaleLowerCase().includes(searchstr.toLocaleLowerCase())
              } else if (typeof b === 'object') {
                return JSON.stringify(b).toLocaleLowerCase().includes(searchstr.toLocaleLowerCase())
              } else {
                return b?.toString().toLocaleLowerCase() === searchstr.toLocaleLowerCase()
              }
            }
            ));
          })
        }
        setSortedSearchResults(_.uniq(_.flatten(filteredDataArray) as Array<SimplifiedBatchSearchResultType>));
        return false;
      }
    } else {
      setSortedSearchResults(searchResults);
    }
  };

  return <>
    <div className='batch-search-page'>
      <div className='batch-search-container sdds-container-fluid'>
        <div className="ta-row sdds-row">
          <div className='ta-col sdds-col-xs-12 sdds-col-sm-5 sdds-col-md-5 sdds-col-lg-5 sdds-col-xlg-5'>
            <div className='sdds-headline-07 sdds-u-mb2 sdds-body-02'>{t("TS_core:BatchSearchInputLabel")} <br /> <span className='sdds-detail-03 batchSearchNote'>{t("TS_core:BatchSearchLabelNote", ['10000', '10000', '10000'])}</span></div>
            {duplicateInfo?.totalDuplicate > 0 && <div className='error-msg tds-body-02'> Duplicates found in search list <span className='duplicate-details' onClick={viewDuplicateInfo} role='button' onKeyDown={viewDuplicateInfo}> ({duplicateInfo?.totalDuplicate})</span></div> }
            <textarea className='sdds-textarea-input sdds-body-02' ref={textAreaRef} title="Search Area" placeholder={t("TS_core:SearchAreaPlaceholder")}></textarea>
          </div>

          <div className='btn-col sdds-no-padding sdds-col-xs-12 sdds-col-sm-2 sdds-col-md-1 sdds-col-lg-1 sdds-col-xlg-1'>
            <div className='flex'>
              <div className='btn-wrapper'>
                <button type='button' className='sdds-btn sdds-btn-primary sdds-btn-fullbleed' onClick={onSearchClick}>{t("TS_core:Search")}</button>
                <button type='button' className='sdds-btn sdds-btn-secondary sdds-btn-fullbleed' onClick={onClearClick}>{t("TS_core:Clear")}</button>
              </div>
            </div>
          </div>
          <div className='sdds-col-md-1-offset sdds-col-xs-12 sdds-col-sm-5 sdds-col-md-5 sdds-col-lg-5 sdds-col-xlg-5'>
            <div className='flex'>
              {/* Next to elements are using flex direction column-reverse. plz don't change their placements */}
              {notFound && notFound.length > 0 && <div className='not-found-wrapper'>
                <div className='not-found-box'>
                  {notFound && notFound.map((stringQuery: string) =>
                    <div className='sdds-body-02'>{stringQuery}</div>
                  )}
                </div>
                <div className='sdds-headline-07 sdds-u-mb1 not-found-label'>{t("TS_core:EquipmentNotFound") + `(${notFound.length})`}<InfoIconWithTooltip uniqueId="SA_status" tooltipText={'Only search inputs with no matches are shown here. Sometimes, a communicator ID input may match a result with a different ID than the one searched due to improper communicator reset; such results are still listed under Equipment found.'} /></div>                
              </div>}
            </div>
          </div>
        </div>
        <div className="dt-row sdds-row">
          <div className='dt-col sdds-col-md-12 sdds-col-sm-12 sdds-col-xs-12 '>
            <div className='dt-container '>
              {<BatchSearchDataTable data={sortedSearchResults} onSortData={onSortData} onSearch={onSearch} />}
              {isLoading && <div className='loader'>
                <div className="spinner">
                  {t("TS_core:LoadingVehicleData")}
                  <Spinner size='md' value={percentage} />
                </div>
              </div>}
            </div>
          </div>
        </div>
        <Toast toastQueue={toastQueue} />
      </div>
    </div>
    <CustomModal
      id='confirm-ssh'
      title={`Total no. of duplicates = ${duplicateInfo?.totalDuplicate}`}
      show={showDuplicateInfo}
      handleClose={hideDuplicateInfo}
      size='sm'
      actions={<></>}
    >
      <div>
        <tds-table responsive={true} compact-design={true}>
          <tds-table-header>
            <tds-header-cell cell-key="input" cell-value="Input"></tds-header-cell>
            <tds-header-cell cell-key="count" cell-value="Occurrence in search list"></tds-header-cell>
          </tds-table-header>
          <tds-table-body>
            {duplicateInfo?.totalDuplicate > 0 && Object.entries(duplicateInfo?.duplicateList as Record<string, number>).map(([key, count], index) => (
              <tds-table-body-row key={index}>
                <tds-body-cell cell-key="input" cell-value={key}></tds-body-cell>
                <tds-body-cell cell-key="count" cell-value={count.toString()}></tds-body-cell>
              </tds-table-body-row>
            ))}
          </tds-table-body>
        </tds-table>
      </div>
    </CustomModal>
  </>
};

export default BatchSearch;
